import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import Icon from 'react-icons-kit';
// import { mic, mic_off, volume_up, volume_off, phone, phone_hangup } from 'react-icons-kit/icomoon';
// import { useToast } from './ToastContext'; // You'll need to implement or replace this
import { toast, Toaster } from "sonner";
import { FaPhoneAlt } from "react-icons/fa";
import { FaPhoneSlash } from "react-icons/fa6";
import { CiMicrophoneOn } from "react-icons/ci";
import { CiMicrophoneOff } from "react-icons/ci";
import { IoVolumeHigh } from "react-icons/io5";
import { IoVolumeMuteSharp } from "react-icons/io5";

const CallerScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // const toast = useToast();
  const socket = useRef(null); // Replace with your WebSocket implementation
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoudspeaker, setIsLoudspeaker] = useState(false);
  const [callStatus, setCallStatus] = useState(location.state?.offer ? 'incoming' : 'connecting');
  const [callDuration, setCallDuration] = useState(0);
  const [localUser, setLocalUser] = useState(null);
  const [micPermission, setMicPermission] = useState(null);
  const [callId, setCallId] = useState(location.state?.callId);
  const [remoteUser, setRemoteUser] = useState(location.state?.user);
  const [offer, setOffer] = useState(location.state?.offer);
  const peerConnection = useRef(null);
  const durationInterval = useRef(null);
  const pendingIceCandidates = useRef([]);

  // Load local user (simulating AsyncStorage)
  useEffect(() => {
    const fetchLocalUser = async () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setLocalUser(parsedUser);
        }
      } catch (error) {
        console.error('Error fetching local user:', error);
        toast('Error loading user data.', { type: 'error' });
      }
    };
    fetchLocalUser();
  }, [toast]);

  // Check microphone permission
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicPermission(true);
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        setMicPermission(false);
        toast('Microphone permission required. Please enable it.', { type: 'error' });
      }
    };
    checkMicPermission();
  }, [toast]);

  // Socket.IO connection and registration
  useEffect(() => {
    if (!socket.current || !location.state?.senderId) return;

    const registerUser = () => {
      if (socket.current.connected) {
        socket.current.emit('register', { userId: location.state.senderId, isUser: true });
      } else {
        socket.current.connect();
      }
    };

    socket.current.on('connect', registerUser);
    socket.current.on('connect_error', (error) => {
      toast('Failed to connect to server. Retrying...', { type: 'error' });
    });
    socket.current.on('register_success', ({ userId }) => {
      console.log(`Registered: ${userId}`);
    });
    socket.current.on('error', ({ message }) => {
      toast(`Socket error: ${message}`, { type: 'error' });
      if (message.includes('Invalid userId')) {
        navigate('/login');
      }
    });

    registerUser();

    return () => {
      socket.current.off('connect');
      socket.current.off('connect_error');
      socket.current.off('register_success');
      socket.current.off('error');
    };
  }, [location.state?.senderId, toast, navigate]);

  // ICE candidate and call termination handling
  useEffect(() => {
    if (!socket.current || !callId) return;

    socket.current.on('ice_candidate', ({ callId: candidateCallId, candidate }) => {
      if (callId === candidateCallId && peerConnection.current) {
        handleIceCandidate(candidate);
      }
    });

    socket.current.on('call_ended', ({ callId: endedCallId }) => {
      if (callId === endedCallId) {
        setCallStatus('ended');
        handleEndCall();
      }
    });

    return () => {
      socket.current.off('ice_candidate');
      socket.current.off('call_ended');
    };
  }, [callId]);

  const startCallDuration = () => {
    durationInterval.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const stopCallDuration = () => {
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCallDuration();
      cleanup();
    };
  }, []);

  const setupWebRTC = async (offerData) => {
    try {
      if (micPermission === false) throw new Error('Microphone permission not granted');
      if (!offerData) throw new Error('No offer provided');

      peerConnection.current = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'turn:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' }
        ]
      });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setLocalStream(stream);
      stream.getTracks().forEach(track => peerConnection.current.addTrack(track, stream));

      peerConnection.current.ontrack = (event) => {
        if (event.streams[0]) {
          setRemoteStream(event.streams[0]);
          setCallStatus('active');
          startCallDuration();
        }
      };

      peerConnection.current.onicecandidate = (event) => {
        if (event.candidate && callId) {
          pendingIceCandidates.current.push(event.candidate.toJSON());
        }
      };

      peerConnection.current.oniceconnectionstatechange = () => {
        const state = peerConnection.current?.iceConnectionState;
        if (state === 'failed' || state === 'disconnected') {
          toast('Call connection failed.', { type: 'error' });
          handleEndCall();
        }
      };

      await peerConnection.current.setRemoteDescription(new RTCSessionDescription(offerData));
      const answer = await peerConnection.current.createAnswer();
      await peerConnection.current.setLocalDescription(answer);

      if (socket.current?.connected && callId) {
        socket.current.emit('call_answer', {
          callId,
          senderId: location.state?.senderId,
          receiverId: remoteUser?.id,
          answer: { sdp: answer.sdp, type: answer.type },
        });

        pendingIceCandidates.current.forEach(candidate => {
          socket.current.emit('ice_candidate', {
            callId,
            senderId: location.state?.senderId,
            receiverId: remoteUser?.id,
            candidate,
          });
        });
        pendingIceCandidates.current = [];
      }
    } catch (error) {
      toast(
        error.message.includes('Permission denied') 
          ? 'Microphone permission required' 
          : `Call setup failed: ${error.message}`,
        { type: 'error' }
      );
      handleEndCall();
    }
  };

  const handleIceCandidate = async (candidate) => {
    try {
      await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.error('ICE candidate error:', error);
    }
  };

  const cleanup = () => {
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    setRemoteStream(null);
    stopCallDuration();
    pendingIceCandidates.current = [];
  };

  const handleEndCall = () => {
    if (socket.current?.connected && callId) {
      socket.current.emit('call_ended', {
        callId,
        senderId: location.state?.senderId,
        receiverId: remoteUser?.id,
      });
    }
    cleanup();
    navigate(-1);
  };

  const acceptCall = async () => {
    if (micPermission === false) {
      toast('Microphone permission required', { type: 'error' });
      return;
    }
    if (!offer || !callId || !remoteUser) {
      toast('Invalid call data', { type: 'error' });
      handleEndCall();
      return;
    }
    setCallStatus('connecting');
    await setupWebRTC(offer);
  };

  const rejectCall = () => {
    if (socket.current?.connected && callId) {
      socket.current.emit('call_rejected', {
        callId,
        senderId: location.state?.senderId,
        receiverId: remoteUser?.id,
      });
    }
    handleEndCall();
  };

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleLoudspeaker = () => {
    setIsLoudspeaker(prev => !prev);
    // Web doesn't need speakerphone control like mobile
  };

  const IncomingCallScreen = ({ user, onAccept, onReject }) => {
    return (
      <div className="flex flex-col items-center justify-between h-screen bg-black p-10">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900 to-black opacity-90"></div>
        
        <div className="relative z-10 animate-pulse">
          {user?.profileimage ? (
            <img 
              src={`http://192.168.1.25:9000/profile/${user.profileimage}`}
              className="w-32 h-32 rounded-full border-2 border-white"
              alt="Profile"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-600 border-2 border-white flex items-center justify-center">
              <span className="text-white text-5xl font-bold">
                {user?.name?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
          )}
        </div>
        
        <div className="text-center z-10">
          <h1 className="text-white text-3xl font-bold mb-2">{user?.name || 'Unknown'}</h1>
          <p className="text-gray-300 text-xl">Incoming Call...</p>
        </div>
        
        <div className="flex justify-between w-full max-w-xs z-10">
          <button 
            onClick={onAccept}
            className="bg-green-500 hover:bg-green-600 rounded-full p-4 flex flex-col items-center w-20 h-20 justify-center"
          >
            <FaPhoneAlt  className="text-white  size-32"  />
            {/* <Icon icon={phone} size={30} className="text-white" /> */}
            <span className="text-white text-sm mt-1">Accept</span>
          </button>
          <button 
            onClick={onReject}
            className="bg-red-500 hover:bg-red-600 rounded-full p-4 flex flex-col items-center w-20 h-20 justify-center"
          >
            <FaPhoneSlash  className="text-white size-32" />
            {/* <Icon icon={phone_hangup} size={30} className="text-white" /> */}
            <span className="text-white text-sm mt-1">Reject</span>
          </button>
        </div>
      </div>
    );
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-screen bg-black">
      <Toaster/>
      {callStatus === 'incoming' && remoteUser ? (
        <IncomingCallScreen user={remoteUser} onAccept={acceptCall} onReject={rejectCall} />
      ) : (
        <div className="flex flex-col items-center justify-center h-full">
          {remoteUser?.profileimage ? (
            <div className="text-center">
              <img
                src={`http://192.168.1.25:9000/profile/${remoteUser.profileimage}`}
                className="w-40 h-40 rounded-full mb-5"
                alt="Profile"
              />
              <h1 className="text-white text-2xl font-bold">{remoteUser?.name}</h1>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-40 h-40 rounded-full bg-gray-600 mb-5 flex items-center justify-center">
                <span className="text-white text-6xl font-bold">
                  {remoteUser?.name?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
              <h1 className="text-white text-2xl font-bold">{remoteUser?.name}</h1>
            </div>
          )}
          
          <div className="absolute top-10 bg-black bg-opacity-50 px-4 py-2 rounded-lg">
            <p className="text-white text-lg">
              {callStatus === 'active' ? 'Connected' : callStatus === 'connecting' ? 'Connecting...' : callStatus}
            </p>
            <p className="text-white text-lg text-center">{formatTime(callDuration)}</p>
          </div>
          
          <div className="absolute bottom-10 flex justify-around w-full max-w-md">
            <button 
              onClick={toggleMute}
              className={`rounded-full p-4 ${isMuted ? 'bg-red-500' : 'bg-gray-600'} hover:bg-opacity-80`}
            >
            {isMuted ? <CiMicrophoneOff className="text-white size-24"/> : <CiMicrophoneOn className="text-white size-24"  />}  
            </button>
            <button 
              onClick={toggleLoudspeaker}
              className={`rounded-full p-4 ${isLoudspeaker ? 'bg-blue-500' : 'bg-gray-600'} hover:bg-opacity-80`}
            >
              {isLoudspeaker ? <IoVolumeHigh className="text-white size-24"  /> : <IoVolumeMuteSharp className="text-white size-24" />} 
            </button>
            <button 
              onClick={handleEndCall}
              className="bg-red-500 hover:bg-red-600 rounded-full p-4"
            >
           <FaPhoneSlash className="text-white size-24"  /> 
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallerScreen;